import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import Toolbar from "../../../CommonComponents/common/Toolbar";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TopSection from "../../../CommonComponents/TopSection";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import CollapsibleHeader from "../../../CommonComponents/common/CollapsibleHeader";
import useRedirect from "../../../CustomHooks/useRedirect";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import OutboundOrderHeader from "./OutboundOrderHeader";
import {
  fetchOrderHeaderData,
  fetchOrderLinesData,
  fetchOrderDeliveryData,
  fetchOrderShipmentData,
} from "../../../services/OutboundOrders/OutboundOrderDetailService";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";
import { formatValue } from "../../../utils/currencyUtils";
import { parseDateValue } from "../../../utils/commonFunctions";




const OutboundOrderDetail = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [allLinesRows, setAllLinesRows] = useState([]);
  const [allDeliveriesRows, setAllDeliveriesRows] = useState([]);
  const [allShipmentsRows, setAllShipmentsRows] = useState([]);
  const [exceptionType, setExceptionType] = useState("");
  // Separate loading states for each section
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const navigate = useNavigate();
  const redirect = useRedirect();
  const { id } = useParams();

  const breadcrumbArray = ["Demand", "Sales Orders", "Sales Orders Detail"];

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoadingDetails(true);
        setLoadingLines(true);
        const header = await fetchOrderHeaderData(id);
        setLoadingDetails(false);
        setHeaderData(header);
        setExceptionType(header?.exceptionType);

        const linesResponse = await fetchOrderLinesData(id);
        const lines = Array.isArray(linesResponse) ? linesResponse : [];

        const formattedLinesRows = lines.map((item) => ({
          lineNo: item.lineNo,
          lob: item.lob,
          itemType: item.itemType,
          item: item.item,
          supplierItemOwner: item.supplierItemOwner,
          itemName: item.itemName,
          materialGroup: item.materialGroup,
          shipFrom: item.shipFrom,
          fromSite: item.fromSite,
          orderQuantity: formatValue(item.orderQuantity),
          orderUom: item.orderUom,
          requestedDate: item.requestedDate,
          totalOpenQty: formatValue(item.totalOpenQty),
          totalConfirmedQty: formatValue(item.totalConfirmedQty),
          readyToShipQty: formatValue(item.readyToShipQty),
          totalShippedQty: formatValue(item.totalShippedQty),
          totalGrQty: formatValue(item.totalGrQty),
          originalConfirmedQty: formatValue(item.originalConfirmedQty),
          originalConfirmedDelvDate: item.originalConfirmedDelvDate,
          poNo: item.poNo,
          lineState: item.lineState,
          // delayReason: item.delayReason,
          cokeRejectionReasonDesc: item.cokeRejectionReasonDesc,
          shipFromOwner: item.shipFromOwner,
          plannedGiDate: item.plannedGiDate,
          materialAvailabilityDate: item.materialAvailabilityDate,
          netWeightItem: formatValue(item.netWeightItem),
          grossWeightItem: formatValue(item.grossWeightItem),
          itemWeightUom: item.itemWeightUom,
          requirementDate: item.requirementDate,
        }));
        setLoadingLines(false);
        setAllLinesRows(formattedLinesRows);

        setLoadingDeliveries(true);
        const deliveriesResponse = await fetchOrderDeliveryData(id);
        const deliveries = Array.isArray(deliveriesResponse)
          ? deliveriesResponse
          : [];
        setLoadingDeliveries(false);
        setAllDeliveriesRows(deliveries);

        setLoadingShipments(true);
        const shipmentsResponse = await fetchOrderShipmentData(id);
        const shipments = Array.isArray(shipmentsResponse)
          ? shipmentsResponse
          : [];
        setLoadingShipments(false);
        setAllShipmentsRows(shipments);
      } catch (error) {
        // No console.error here!
      }
    };
    fetchData();
  }, [id]);

  const redirectFn = useMemo(() => {
    return (path, state) => {
      redirect(path, state);
    };
  }, [redirect]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/core/demand/sales-orders");
    }
  };

  const toolbarProps = {
    number: id,
    status: exceptionType,
    icon: <ReceiptLongIcon />,
    onBack: handleBack,
    isCollapsed,
    toggleHeader: () => setIsCollapsed((prev) => !prev),
  };

  const linesTab = useMemo(() => ({
    label: `Lines (${allLinesRows.length})`,
    loading: loadingLines,
    columns: [
      { field: "lineNo", headerName: "Line No" },
      // { field: "lob", headerName: "LOB" },
      // { field: "itemType", headerName: "Item Type" },
      {
        field: "item",
        headerName: "Material Number",
        cellRenderer: (params) => {
          const { value, data } = params;
          const redirect = params.colDef.cellRendererParams?.redirect;
          const siteCode = data?.shipFrom ?? " ";
          return (
            <span
              onClick={() =>
                redirect(
                  `/core/inventory/node/inventory/inventory-detail/${value}/${siteCode}`,
                  {
                    menu: "Inventory",
                    label: "Inventory List",
                    outerLabel: "Inventory Detail",
                    previousPage: window.location.pathname,
                  }
                )
              }
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {value}
            </span>
          );
        },
        cellRendererParams: { redirect: redirectFn },
      },
      // { field: "supplierItemOwner", headerName: "Supplier Item Owner" },
      {
        field: "itemName",
        headerName: "Material Description",
      },
      { field: "materialGroup", headerName: "Material Group" },
      { field: "shipFrom", headerName: "Plant Number" },
      { field: "fromSite", headerName: "Plant Name" },
      { field: "orderQuantity", headerName: "Order Qty" },
      { field: "orderUom", headerName: "Uom" },
      { field: "requestedDate", headerName: "Requested Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
      { field: "totalOpenQty", headerName: "Total Open Qty" },
      { field: "totalConfirmedQty", headerName: "Total Confirmed Qty" },
      // { field: "readyToShipQty", headerName: "Ready to Ship Qty" },
      { field: "totalShippedQty", headerName: "Total Shipped Qty" },
      // { field: "totalGrQty", headerName: "Total Gr Qty" },
      { field: "originalConfirmedQty", headerName: "Original Confirmed Qty" },
      {
        field: "originalConfirmedDelvDate",
        headerName: "Original Confirmed Delv Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB),
      },
      { field: "poNo", headerName: "PO No" },
      { field: "lineState", headerName: "Line State" },
      // { field: "delayReason", headerName: "Delay Reason" },
      { field: "cokeRejectionReasonDesc", headerName: "Coke Rejection Reason Description" },
      { field: "shipFromOwner", headerName: "Ship From Owner" },
      { field: "plannedGiDate", headerName: "Planned GI Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
      {
        field: "materialAvailabilityDate",
        headerName: "Material Availability Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB),
      },
      { field: "netWeightItem", headerName: "Net Weight Item" },
      { field: "grossWeightItem", headerName: "Gross Weight Item" },
      { field: "itemWeightUom", headerName: "Item Weight Uom" },
      { field: "requirementDate", headerName: "Requirement Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
    ],
    rows: allLinesRows,
    // toolbar: {
    //   buttons: [
    //     {
    //       label: "Save Order",
    //       onClick: () => {},
    //       disabled: true,
    //     },
    //     {
    //       label: "Update Delay Reason",
    //       onClick: () => {},
    //       disabled: true,
    //     },
    //   ],
    // },
  }), [allLinesRows, loadingLines, redirectFn]
  );

  const deliveriesTab = useMemo(() => ({
    label: `Deliveries (${allDeliveriesRows.length})`,
    loading: loadingDeliveries,
    columns: [
      {
        field: "deliveryNo",
        headerName: "Delivery No",
        cellRenderer: (params) => {
          const { value, data } = params;
          const redirect = params.colDef.cellRendererParams?.redirect;
          return (
            <span
              onClick={() =>
                redirect(`/core/demand/delivery/delivery-detail/${value}/${data?.shipFrom}`, {
                  menu: "Demand",
                  label: "Outbound Deliveries",
                  outerLabel: "Outbound Deliveries Detail",
                  previousPage: window.location.pathname,
                })
              }
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {value}
            </span>
          );
        },
        cellRendererParams: { redirect: redirectFn },
      },
      { field: "deliveryType", headerName: "Delivery Type" },
      { field: "lob", headerName: "LOB" },
      { field: "supplier", headerName: "Supplier" },
      {
        field: "supplierName",
        headerName: "Supplier Name",
      },
      { field: "customer", headerName: "Customer" },
      { field: "customerName", headerName: "Customer Name" },
      { field: "shipFrom", headerName: "Ship From" },
      { field: "shipFromOwner", headerName: "Ship From Owner" },
      { field: "shipFromSite", headerName: "Ship From (Site)" },
      { field: "shipTo", headerName: "Ship To" },
      { field: "shipToOwner", headerName: "Ship To Owner" },
      { field: "shipToSite", headerName: "Ship To (Site)" },
      { field: "erpHeaderStatus", headerName: "ERP Header Status" },
      { field: "shipmentNo", headerName: "Shipment No" },
      { field: "shipmentLineNo", headerName: "Shipment Line No" },
      { field: "shipMode", headerName: "Ship Mode" },
      // { field: "bolNo", headerName: "BOL No" },
      // { field: "hawbNo", headerName: "HAWB No" },
      { field: "carrier", headerName: "Carrier" },
      // { field: "containerNo", headerName: "Container No" },
      // { field: "packingSlipNo", headerName: "Packing Slip No" },
      {
        field: "creationDate", headerName: "Creation Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "plannedShipDate", headerName: "Planned Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "actualShipDate", headerName: "Actual Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "eta", headerName: "ETA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "ata", headerName: "ATA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      // { field: "truckNo", headerName: "Truck No" },
      // { field: "trailerNo", headerName: "Trailer No" },
      // { field: "comments", headerName: "Comments" },
      { field: "highLevelItem", headerName: "High Level Item" },
      // { field: "shippingPoint", headerName: "Shipping Point" },
      // { field: "palletNo", headerName: "Pallet No" },
    ],
    rows: allDeliveriesRows,
  }), [allDeliveriesRows, loadingDeliveries, redirectFn]
  );

  const shipmentsTab = useMemo(() => ({
    label: `Shipments (${allShipmentsRows.length})`,
    loading: loadingShipments,
    columns: [
      {
        field: "shipmentNo",
        headerName: "Shipment No",
        cellRenderer: (params) => {
          const { value } = params;
          const redirect = params.colDef.cellRendererParams?.redirect;
          return (
            <div
              style={{
                borderLeft: "5px solid black",
                paddingLeft: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              <LocalShippingOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              <span
                onClick={() =>
                  redirect(`/core/demand/shipment/shipment-detail/${value}`, {
                    menu: "Demand",
                    label: "Outbound Shipments",
                    outerLabel: "Outbound Shipments Detail",
                    previousPage: window.location.pathname,
                  })
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            </div>
          );
        },
        cellRendererParams: { redirect: redirectFn },
      },
      { field: "supplier", headerName: "Supplier" },
      { field: "customer", headerName: "Customer" },
      { field: "carrier", headerName: "Carrier" },
      {
        field: "plannedShipDate", headerName: "Planned Ship Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      { field: "state", headerName: "Header State" },
      {
        field: "containereta", headerName: "ETA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "containerPTA", headerName: "PTA",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      {
        field: "creationDate", headerName: "Creation Date",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
      // { field: "notes", headerName: "Notes" },
      {
        field: "lmd", headerName: "Last Modified",
        comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)
      },
    ],
    rows: allShipmentsRows,
  }), [allShipmentsRows, loadingShipments, redirectFn]
  );

  const tabs = [linesTab, deliveriesTab, shipmentsTab];

  return (
    <div>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={true}
        headerName={""}
        hideTitle={true}
      />
      <Toolbar {...toolbarProps} />
      <CollapsibleHeader
        upperLayer={
          <Box sx={{ position: "relative" }}>
            {loadingDetails && <SectionSpinner />}
            <OutboundOrderHeader hProps={headerData} />
          </Box>
        }
        lowerLayer={<DetailsPageLowerLayer tabs={tabs} />}
        isCollapsed={isCollapsed}
      />
    </div>
  );
};

export default OutboundOrderDetail;
